import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useInvoice,
  useDeleteInvoice,
  useInvoiceHistory,
  useUpdateInvoice,
  useMarkInvoicePaid,
  useUnmarkInvoicePaid,
  useDownloadInvoiceTimesheetXlsx,
} from '../../hooks/useInvoices';
import { Button } from '../../components/common/Button';
import { Spinner } from '../../components/common/Spinner';
import { Modal } from '../../components/common/Modal';
import { DismissibleErrorBanner } from '../../components/common/DismissibleErrorBanner';
import { formatCurrency, formatDisplayDate, formatDisplayDateTime } from '../../utils/format';
import {
  derivedDueDateMarker,
  formatInvoiceDueDate,
  hasDerivedDueDate,
} from '../../utils/invoiceDueDate';
import { StateHistory } from '../../components/workflow/StateHistory';
import { InvoiceBreakdownTable } from '../../components/invoices/InvoiceBreakdownTable';
import { useFlag } from '../../services/flagsContext';
import { DateField } from '../../components/ui/DateField';
import type { InvoiceHistoryEntry } from '../../services/api/invoices';

/**
 * MKS-258 — today in Europe/Prague as `YYYY-MM-DD`, the latest day an invoice
 * can have been paid on. The company day, not the browser's: the backend
 * applies the same ceiling, so a user in another timezone must see the same
 * last selectable day the server would accept.
 */
function pragueToday(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Prague' }).format(new Date());
}

/**
 * MKS-352 — one invoice-history row in the shape `<StateHistory>` renders.
 *
 * The two namespaces the endpoint merges carry different `changes` payloads.
 * The `Invoice` rows already hold the `FieldChange[]` the component expects.
 * The Phase 110 `invoice_discount_override` rows hold a flat object
 * (`{ projectId, configuredPercentage, appliedPercentage, amountCzk }`),
 * which the component would silently render as nothing — so it is turned into
 * the two statements a reader actually wants: what the percentage was changed
 * to, and what that gave away.
 */
function toHistoryItem(entry: InvoiceHistoryEntry) {
  if (entry.subjectType !== 'invoice_discount_override' || Array.isArray(entry.changes)) {
    return {
      ...entry,
      changes: Array.isArray(entry.changes) ? entry.changes : null,
    };
  }

  const payload = (entry.changes ?? {}) as {
    configuredPercentage?: unknown;
    appliedPercentage?: unknown;
    amountCzk?: unknown;
  };
  return {
    ...entry,
    state: 'discount_override_applied',
    changes: [
      {
        field: 'discount %',
        oldValue: payload.configuredPercentage ?? null,
        newValue: payload.appliedPercentage ?? null,
      },
      { field: 'discount (CZK)', oldValue: null, newValue: payload.amountCzk ?? null },
    ],
  };
}

export default function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const invoiceId = id ? parseInt(id, 10) : undefined;
  const { data: invoice, isLoading, error } = useInvoice(invoiceId);
  // MKS-352 — the invoice-scoped history endpoint, which merges the
  // discount-override rows in and is open to finance (the generic audit trail
  // this page used to read is not).
  const { data: invoiceHistory } = useInvoiceHistory(invoiceId);
  const history = (invoiceHistory ?? []).map(toHistoryItem);
  const deleteMutation = useDeleteInvoice();
  const updateMutation = useUpdateInvoice();
  const markPaidMutation = useMarkInvoicePaid();
  const unmarkPaidMutation = useUnmarkInvoicePaid();
  // MKS-350 — the client timesheet export.
  const timesheetExportOn = useFlag('mks_350_invoice_timesheet_export');
  const timesheetExportMutation = useDownloadInvoiceTimesheetXlsx();
  const [exportAmounts, setExportAmounts] = useState(true);
  const [exportTask, setExportTask] = useState(true);
  // What was worked on is the point of a client timesheet, so descriptions are
  // in by default; unticking drops the column for this one export.
  const [exportDescriptions, setExportDescriptions] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // MKS-352 — what finance types into the delete dialog. Required: the Delete
  // button in the dialog stays disabled until it holds something.
  const [deleteReason, setDeleteReason] = useState('');
  // F2-005: surface delete + save rejections inline. Previously the
  // ConfirmModal stayed open on a rejected delete and the save button gave
  // no feedback on rejection (only an unhandled rejection + Sentry tag).
  const [actionError, setActionError] = useState<string | null>(null);

  // Editable fields
  const [editName, setEditName] = useState('');
  const [editInvoiceNumber, setEditInvoiceNumber] = useState('');
  const [editOrderNumber, setEditOrderNumber] = useState('');
  // MKS-214 — '' means "no due date"; the PATCH turns it back into null.
  const [editDueDate, setEditDueDate] = useState('');
  // The date finance is marking the invoice paid on. Empty = today, resolved
  // server-side so two people in different timezones agree on the day.
  const [paidAtInput, setPaidAtInput] = useState('');

  // Sync form with loaded invoice
  useEffect(() => {
    if (invoice) {
      setEditName(invoice.name || '');
      setEditInvoiceNumber(invoice.invoiceNumber || '');
      setEditOrderNumber(invoice.orderNumber || '');
      setEditDueDate(invoice.dueDate || '');
    }
  }, [invoice]);

  const handleDelete = async () => {
    if (!invoiceId) return;
    // MKS-352 — the same check the server makes. Whitespace is not a reason,
    // so trim before deciding, and send the trimmed text.
    const reason = deleteReason.trim();
    if (reason.length === 0) return;
    setActionError(null);
    try {
      await deleteMutation.mutateAsync({ id: invoiceId, reason });
      setShowDeleteConfirm(false);
      setDeleteReason('');
      // The invoice still exists, so land on the deletion log rather than the
      // list it has just disappeared from.
      navigate('/invoices?deleted=1');
    } catch (err) {
      setActionError((err as Error).message || 'Failed to delete invoice');
      setShowDeleteConfirm(false);
    }
  };

  const handleSave = async () => {
    if (!invoiceId || !invoice) return;
    // MKS-214 — `dueDate` is the one field that can be sent as null (to clear
    // it), so the accumulator is no longer string-only.
    const changes: Record<string, string | null> = {};
    if (editName !== (invoice.name || '')) changes.name = editName;
    if (editInvoiceNumber !== (invoice.invoiceNumber || '')) changes.invoiceNumber = editInvoiceNumber;
    if (editOrderNumber !== (invoice.orderNumber || '')) changes.orderNumber = editOrderNumber;
    if (editDueDate !== (invoice.dueDate || '')) {
      changes.dueDate = editDueDate || null;
    }
    if (Object.keys(changes).length === 0) return;
    setActionError(null);
    try {
      await updateMutation.mutateAsync({ id: invoiceId, ...changes });
    } catch (err) {
      setActionError((err as Error).message || 'Failed to save invoice');
    }
  };

  const handleMarkPaid = async () => {
    if (!invoiceId) return;
    setActionError(null);
    try {
      await markPaidMutation.mutateAsync({ id: invoiceId, paidAt: paidAtInput || undefined });
      setPaidAtInput('');
    } catch (err) {
      setActionError((err as Error).message || 'Failed to mark invoice paid');
    }
  };

  // MKS-350 — download the client timesheet with whatever is ticked right now.
  const handleExportTimesheet = async () => {
    if (!invoiceId) return;
    setActionError(null);
    try {
      await timesheetExportMutation.mutateAsync({
        id: invoiceId,
        options: {
          includeAmounts: exportAmounts,
          includeTask: exportTask,
          includeDescriptions: exportDescriptions,
        },
      });
    } catch (err) {
      setActionError((err as Error).message || 'Failed to export the timesheet');
    }
  };

  const handleUnmarkPaid = async () => {
    if (!invoiceId) return;
    setActionError(null);
    try {
      await unmarkPaidMutation.mutateAsync(invoiceId);
    } catch (err) {
      setActionError((err as Error).message || 'Failed to undo the payment marking');
    }
  };

  const hasChanges = invoice && (
    editName !== (invoice.name || '') ||
    editInvoiceNumber !== (invoice.invoiceNumber || '') ||
    editOrderNumber !== (invoice.orderNumber || '') ||
    editDueDate !== (invoice.dueDate || '')
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner />
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error ? (error as Error).message : 'Invoice not found'}</p>
        <Button variant="secondary" className="mt-2" onClick={() => navigate('/invoices')}>
          Back to Invoices
        </Button>
      </div>
    );
  }

  // MKS-352 — a cancelled invoice renders read-only: the banner, the period,
  // the amount and the breakdown snapshot, and not one control that would
  // write to it. Keyed on `deletedAt` alone — `deleteReason` is free text and
  // `deletedByName` can be missing, so neither is a safe signal.
  const isDeleted = invoice.deletedAt != null;

  // Handle both new format (byProject array) and legacy Rails format (projects object)
  const breakdown = invoice.data;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const breakdownAny = breakdown as any;
  const hasNewFormatBreakdown = breakdown && Array.isArray(breakdown.byProject) && breakdown.byProject.length > 0;
  const hasLegacyBreakdown = breakdownAny && !breakdownAny.byProject && breakdownAny.projects;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button onClick={() => navigate('/invoices')} className="text-sm text-gray-500 hover:text-gray-700 mb-1">
            &larr; Back to Invoices
          </button>
          <h1 className="text-2xl font-semibold text-gray-900">{invoice.name || 'Invoice'}</h1>
        </div>
        {!isDeleted && (
          <Button variant="danger" size="sm" onClick={() => setShowDeleteConfirm(true)}>
            Delete
          </Button>
        )}
      </div>

      <DismissibleErrorBanner message={actionError} onDismiss={() => setActionError(null)} />

      {/* MKS-352 — the deleted banner. Petra 2026-10-15: "there must be a log
          of who deleted it" — this is that log for the one invoice somebody
          has opened, answering who, when and why before anything else on the
          page. */}
      {isDeleted && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm font-semibold text-red-800">
            This invoice was deleted
            {invoice.deletedByName ? ` by ${invoice.deletedByName}` : ''}
            {invoice.deletedAt ? ` on ${formatDisplayDateTime(invoice.deletedAt)}` : ''}.
          </p>
          {invoice.deleteReason && (
            <p className="mt-1 text-sm text-red-700">
              <span className="font-medium">Reason:</span> {invoice.deleteReason}
            </p>
          )}
          <p className="mt-2 text-sm text-red-700">
            It counts towards no report or revenue figure, and its entries, expense receipts and
            billing periods have been released. It cannot be edited or restored.
          </p>
        </div>
      )}

      {/* MKS-352 — the read-only face of a cancelled invoice: the same facts
          the editable card carries, with nothing that writes. Deliberately
          NOT the editable card with `disabled` inputs — a greyed-out form
          reads as "temporarily unavailable" rather than "this is final". */}
      {isDeleted && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-500">Invoice Name</p>
              <p className="font-medium text-gray-900">{invoice.name || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Invoice Number</p>
              <p className="font-medium text-gray-900">{invoice.invoiceNumber || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Order Number</p>
              <p className="font-medium text-gray-900">{invoice.orderNumber || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Due Date</p>
              {/* MKS-454 — a derived date is shown with its term; '-' is the
                  flag-off text, and on an invoice that has a real due date. */}
              <p className="font-medium text-gray-900">{formatInvoiceDueDate(invoice)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Period</p>
              <p className="font-medium text-gray-900">
                {invoice.from && invoice.to
                  ? `${formatDisplayDate(invoice.from)} - ${formatDisplayDate(invoice.to)}`
                  : '-'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Amount</p>
              <p className="font-medium text-gray-900">{formatCurrency(invoice.invoicedAmount)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Editable Details Card */}
      {!isDeleted && (
      <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Name</label>
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Number</label>
            <input
              type="text"
              value={editInvoiceNumber}
              onChange={(e) => setEditInvoiceNumber(e.target.value)}
              placeholder="Optional"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Order Number</label>
            <input
              type="text"
              value={editOrderNumber}
              onChange={(e) => setEditOrderNumber(e.target.value)}
              placeholder="Optional"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="invoice-due-date" className="block text-sm font-medium text-gray-700 mb-1">
              Due Date
            </label>
            <DateField
              id="invoice-due-date"
              value={editDueDate}
              onChange={setEditDueDate}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {/* MKS-454 — the field itself stays EMPTY: the derived date is
                computed on every read and never written to the column, so
                saving this form must not silently store it. The hint says
                what the invoice is being aged from in the meantime, and
                typing a real date here replaces it. */}
            {hasDerivedDueDate(invoice) && (
              <p className="mt-1 text-xs text-gray-500">
                No due date on the invoice — aged from{' '}
                {formatDisplayDate(invoice.effectiveDueDate)}{' '}
                {derivedDueDateMarker(invoice.derivedNetDays)}
              </p>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-500">Period</p>
            <p className="font-medium text-gray-900">
              {invoice.from && invoice.to ? `${formatDisplayDate(invoice.from)} - ${formatDisplayDate(invoice.to)}` : '-'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Total Amount</p>
            <p className="font-medium text-gray-900">{formatCurrency(invoice.invoicedAmount)}</p>
          </div>
        </div>
        {hasChanges && (
          <div className="flex gap-2 pt-2 border-t border-gray-100">
            <Button size="sm" onClick={handleSave} isLoading={updateMutation.isPending}>
              Save Changes
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setEditName(invoice.name || '');
                setEditInvoiceNumber(invoice.invoiceNumber || '');
                setEditOrderNumber(invoice.orderNumber || '');
              }}
            >
              Cancel
            </Button>
          </div>
        )}
      </div>
      )}

      {/* MKS-214 — payment status card. The server derives `status`; this card
          only renders it and offers the one action that applies.
          MKS-352 — a cancelled invoice has no payment to mark or undo: the
          server refuses both (409), so the card goes rather than offering a
          button that cannot work. */}
      {!isDeleted && (
      <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Payment</p>
            <p className="font-medium text-gray-900">
              {/* MKS-454 — "since" / "due" read the date the server actually
                  decided `status` from, so an invoice this card calls overdue
                  can never also claim it has no due date. The marker says
                  where a derived date came from. */}
              {invoice.status === 'paid'
                ? `Paid on ${formatDisplayDate(invoice.paidAt)}`
                : invoice.status === 'overdue'
                  ? `Overdue since ${formatInvoiceDueDate(invoice)}`
                  : invoice.dueDate || hasDerivedDueDate(invoice)
                    ? `Unpaid, due ${formatInvoiceDueDate(invoice)}`
                    : 'Unpaid, no due date'}
            </p>
          </div>
          {invoice.status === 'paid' ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={handleUnmarkPaid}
              isLoading={unmarkPaidMutation.isPending}
            >
              Undo Payment
            </Button>
          ) : (
            <div className="flex items-end gap-2">
              <div>
                <label htmlFor="invoice-paid-at" className="block text-sm font-medium text-gray-700 mb-1">
                  Payment date
                </label>
                <DateField
                  id="invoice-paid-at"
                  value={paidAtInput}
                  max={pragueToday()}
                  onChange={setPaidAtInput}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <Button size="sm" onClick={handleMarkPaid} isLoading={markPaidMutation.isPending}>
                Mark Paid
              </Button>
            </div>
          )}
        </div>
      </div>
      )}

      {/* MKS-350 — the client timesheet export. Petra 2026-10-15: agencies send
          their clients an hours report with the invoice. Behind
          `mks_350_invoice_timesheet_export` until somebody has checked a real
          export against a real invoice.
          MKS-352 — never offered for a cancelled invoice: its entries were
          released when it was cancelled, so the workbook would be an empty
          client-facing document (the server 404s it for the same reason). */}
      {timesheetExportOn && !isDeleted && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Client timesheet</h3>
            <p className="text-sm text-gray-500">
              Every hour billed on this invoice — who, when, what — as an Excel workbook to send
              with the invoice. Contains no internal costs.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={exportAmounts}
                onChange={(e) => setExportAmounts(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              Include rates and amounts
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={exportTask}
                onChange={(e) => setExportTask(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              Include task
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={exportDescriptions}
                onChange={(e) => setExportDescriptions(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              Include descriptions
            </label>
          </div>
          <Button
            size="sm"
            onClick={handleExportTimesheet}
            isLoading={timesheetExportMutation.isPending}
          >
            Export timesheet (Excel)
          </Button>
        </div>
      )}

      {/* Breakdown - New Format (shared with /invoices/new preview) */}
      {hasNewFormatBreakdown && (
        <InvoiceBreakdownTable breakdown={breakdown} title="Breakdown" />
      )}

      {/* Breakdown - Legacy Rails Format */}
      {hasLegacyBreakdown && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h3 className="text-sm font-medium text-gray-900">Breakdown (Legacy)</h3>
          </div>
          {Object.entries(breakdownAny.projects as Record<string, { name: string; total: number; rates?: Record<string, { name: string; hours: number; total: number }> }>).map(([projectId, project]) => (
            <div key={projectId} className="border-b border-gray-100 last:border-b-0">
              <div className="px-6 py-3 bg-gray-50/50">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-900">{project.name}</span>
                  <span className="text-sm text-gray-600">{formatCurrency(project.total)}</span>
                </div>
              </div>
              {project.rates && (
                <table className="min-w-full">
                  <thead>
                    <tr className="text-xs text-gray-500 uppercase">
                      <th className="px-6 py-2 text-left">Rate</th>
                      <th className="px-6 py-2 text-right">Hours</th>
                      <th className="px-6 py-2 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(project.rates).map(([rateId, rate]) => (
                      <tr key={rateId} className="border-t border-gray-50">
                        <td className="px-6 py-2 text-sm text-gray-900">{rate.name}</td>
                        <td className="px-6 py-2 text-sm text-gray-600 text-right">{rate.hours.toFixed(1)}</td>
                        <td className="px-6 py-2 text-sm text-gray-900 text-right font-medium">{formatCurrency(rate.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
        </div>
      )}

      {/* MKS-352 — the delete dialog, with a REQUIRED reason. A plain
          ConfirmModal cannot carry an input, so this is the base Modal with
          its own footer; the Delete button stays disabled until the box holds
          something other than whitespace, which is the same rule the server
          applies. */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setDeleteReason('');
        }}
        title="Delete Invoice"
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                setShowDeleteConfirm(false);
                setDeleteReason('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              disabled={deleteReason.trim().length === 0}
              isLoading={deleteMutation.isPending}
            >
              Delete
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            This releases the invoice&apos;s time entries, expense receipts and billing periods so
            they can be invoiced again, and takes it out of every list, report and revenue figure.
            It stays visible in Deleted invoices with your name and the reason below. It cannot be
            restored.
          </p>
          <div>
            <label
              htmlFor="invoice-delete-reason"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Reason <span className="text-red-600">*</span>
            </label>
            <textarea
              id="invoice-delete-reason"
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="Why is this invoice being deleted?"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>
      </Modal>

      {/* History */}
      <div className="mt-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">History</h3>
        <StateHistory history={history} />
      </div>
    </div>
  );
}
